import React, { ReactNode, Suspense } from 'react'

import ErrorLayout from 'lib/layouts/ErrorLayout'
import { errorTracking } from 'utils/posthog'

interface Props {
  children?: ReactNode
}

interface State {
  hasError: boolean
}

// Replace high-cardinality path segments (UUIDs, long numeric IDs) with :id so
// crashes on the same screen group into one issue instead of one per record.
const normalizeRoute = (pathname: string): string =>
  pathname
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ':id'
    )
    .replace(/\b\d{4,}\b/g, ':id')

// Pull the nearest application component out of React's component stack so the
// fingerprint can distinguish which subtree threw. Best-effort: component
// names are minified in production, so it falls back to the first frame.
const topComponentFrame = (
  componentStack?: string | null
): string | undefined => {
  if (!componentStack) return undefined
  const names = componentStack
    .split('\n')
    .map(line => line.trim().match(/^(?:at|in)\s+([^\s(]+)/)?.[1])
    .filter((name): name is string => Boolean(name))
  if (names.length === 0) return undefined
  return names.find(name => /^[A-Z]/.test(name)) ?? names[0]
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props | Readonly<Props>) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Caught error:', error, errorInfo)

    // route + top component split the otherwise catch-all
    // `ErrorBoundary:<name>` bucket into per-screen issues (e.g. the
    // translate-induced `removeChild` NotFoundError on /dashboard no longer
    // merges with unrelated crashes elsewhere).
    const route =
      typeof window !== 'undefined'
        ? normalizeRoute(window.location.pathname)
        : undefined

    errorTracking({
      error,
      properties: {
        componentStack: errorInfo.componentStack,
        route,
        component: topComponentFrame(errorInfo.componentStack)
      },
      fingerprint: `ErrorBoundary:${error.name}`
    })
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <Suspense fallback={null}>
        <ErrorLayout
          message='An unknown error occurred, please try again.'
          title='An error occurred'
          handleTryAgain={() => this.setState({ hasError: false })}
        />
      </Suspense>
    )
  }
}

export default ErrorBoundary
